import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useQuery } from "@apollo/client"
import { UserContext } from "../context/userContext"
import { GET_ORGANIZATIONS_IDSTAMP } from "../nav/NavMain/queries"

const EMPTY_LABELS = []

function normalizeMenuItem(item = {}) {
  // Normalize icon values: keep trimmed strings, map empty/non-string/"none" to null.
  const normalizedIcon = typeof item?.icon === "string" ? item.icon.trim() : ""

  const normalizedItem = {
    ...item,
    icon:
      normalizedIcon && normalizedIcon.toLowerCase() !== "none"
        ? normalizedIcon
        : null,
  }

  if (Array.isArray(item?.items)) {
    normalizedItem.items = item.items.map((child) => normalizeMenuItem(child))
  }

  return normalizedItem
}

function filterTopLevelMenu(menu = [], includeLabels = [], excludeLabels = []) {
  // Apply top-level include/exclude filters only; child items are left untouched.
  const hasIncludeLabels =
    Array.isArray(includeLabels) && includeLabels.length > 0
  const hasExcludeLabels =
    Array.isArray(excludeLabels) && excludeLabels.length > 0

  // Guard against unexpected payload shapes from Firestore.
  let filteredMenu = Array.isArray(menu) ? menu : []

  // Include runs first so callers can request a subset explicitly.
  if (hasIncludeLabels) {
    filteredMenu = filteredMenu.filter((item) =>
      includeLabels.includes(item?.label),
    )
  }

  // Exclude runs second and wins if a label appears in both lists.
  if (hasExcludeLabels) {
    filteredMenu = filteredMenu.filter(
      (item) => !excludeLabels.includes(item?.label),
    )
  }

  return filteredMenu
}

export default function useMainMenu(options = {}) {
  const userCtx = useContext(UserContext)
  const menuRef = userCtx?.session?.menu
  const [menuItems, setMenuItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Supported context replacements for menu paths.
  const enterpriseId = userCtx?.session?.user?.enterprise_id
  const enterpriseIdStamp = userCtx?.session?.user?.enterprise_id_stamp
  const staffId = userCtx?.session?.user?.staff_id
  const organizationId = userCtx?.session?.user?.organization_id

  const { data: organizationStampData } = useQuery(GET_ORGANIZATIONS_IDSTAMP, {
    variables: {
      staff_id: staffId,
      organization_id: organizationId,
    },
    skip: !staffId || !organizationId,
    fetchPolicy: "cache-first",
  })

  // Source organization_id_stamp from the org lookup query for path replacement.
  const organizationIdStampFromLookup = useMemo(() => {
    const organizations = organizationStampData?.organizations || []
    const matchedOrganization = organizations.find(
      (org) => String(org?.organization_id) === String(organizationId),
    )

    return matchedOrganization?.organization_id_stamp
  }, [organizationId, organizationStampData])

  // Replaces supported path placeholders with values from the current user context.
  const resolveMenuPath = useCallback(
    (path) => {
      if (typeof path !== "string") {
        return path
      }

      let resolvedPath = path
      const replacements = {
        enterprise_id: enterpriseId,
        enterprise_id_stamp: enterpriseIdStamp,
        organization_id: organizationId,
        organization_id_stamp: organizationIdStampFromLookup,
      }

      // Replace placeholders in the menu path with actual values from the user context.
      Object.entries(replacements).forEach(([placeholder, value]) => {
        if (value) {
          resolvedPath = resolvedPath.replace(
            new RegExp(`\\{${placeholder}\\}`, "g"),
            value,
          )
        }
      })

      return resolvedPath
    },
    [
      enterpriseId,
      enterpriseIdStamp,
      organizationId,
      organizationIdStampFromLookup,
    ],
  )

  // Normalize optional inputs so downstream logic always works with arrays.
  const includeLabelsKey = useMemo(() => {
    const includeLabels = Array.isArray(options.includeLabels)
      ? options.includeLabels
      : EMPTY_LABELS

    return JSON.stringify(includeLabels)
  }, [options.includeLabels])

  const excludeLabelsKey = useMemo(() => {
    const excludeLabels = Array.isArray(options.excludeLabels)
      ? options.excludeLabels
      : EMPTY_LABELS

    return JSON.stringify(excludeLabels)
  }, [options.excludeLabels])

  useEffect(() => {
    // If session is not ready yet, expose an empty non-loading state.
    if (!menuRef?.onSnapshot) {
      setMenuItems([])
      setLoading(false)
      setError(null)
      return undefined
    }

    setLoading(true)
    setError(null)

    const includeLabels = JSON.parse(includeLabelsKey)
    const excludeLabels = JSON.parse(excludeLabelsKey)

    // Single source subscription for all menu consumers.
    const unsubscribe = menuRef.onSnapshot(
      (snapshot) => {
        const fullMenu = snapshot.data()?.menu || []
        const normalizedMenu = Array.isArray(fullMenu)
          ? fullMenu.map((item) => normalizeMenuItem(item))
          : []
        const filteredMenu = filterTopLevelMenu(
          normalizedMenu,
          includeLabels,
          excludeLabels,
        )

        // Publish filtered top-level sections in source order.
        setMenuItems(filteredMenu)
        setLoading(false)
      },
      (err) => {
        // Keep consumers safe by returning empty data on subscription failure.
        setError(err)
        setMenuItems([])
        setLoading(false)
      },
    )

    // Always detach previous listener when dependencies change or component unmounts.
    return () => unsubscribe()
  }, [excludeLabelsKey, includeLabelsKey, menuRef])

  return {
    menuItems,
    loading,
    error,
    resolveMenuPath,
  }
}
